import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { userLocation } from '../../util/maps';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { Heading } from '../../components';
import GeocoderMapbox from '../../components/LocationAutocompleteInput/GeocoderMapbox';
import GeocoderGoogleMaps from '../../components/LocationAutocompleteInput/GeocoderGoogleMaps';

import { formatListingFieldValue, getListingFieldEntry } from './listingFieldGroups';
import {
  STATIC_MAP_MAX_WIDTH,
  distanceInMiles,
  roundMiles,
  staticJourneyMapUrl,
} from './dispatchLocation';
import css from './ListingPage.module.css';

// The Mapbox library is loaded with `defer` on the product page, so it may not
// be there yet when this section mounts. Check for it a few times before giving
// up and showing the dispatch location as text only.
const LIB_POLL_INTERVAL_MS = 300;
const LIB_POLL_ATTEMPTS = 20;

// Seller-typed places repeat across listings (and across visits to the same
// listing), so geocode each one at most once per page load.
const geocodeCache = new Map();

const isGoogleMaps = mapsConfig => mapsConfig?.mapProvider === 'googleMaps';

const isGeocoderLibLoaded = mapsConfig =>
  typeof window !== 'undefined' &&
  (isGoogleMaps(mapsConfig)
    ? !!window.google?.maps?.places
    : !!(window.mapboxgl && window.mapboxSdk));

const waitForGeocoder = mapsConfig =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      if (isGeocoderLibLoaded(mapsConfig)) {
        if (!isGoogleMaps(mapsConfig) && !window.mapboxgl.accessToken) {
          window.mapboxgl.accessToken = mapsConfig.mapboxAccessToken;
        }
        const Geocoder = isGoogleMaps(mapsConfig) ? GeocoderGoogleMaps : GeocoderMapbox;
        resolve(new Geocoder());
      } else if (++attempts >= LIB_POLL_ATTEMPTS) {
        reject(new Error('Map library not available'));
      } else {
        window.setTimeout(check, LIB_POLL_INTERVAL_MS);
      }
    };
    check();
  });

/**
 * Resolve a free-text place ("Bristol, UK", "BS1 4DJ") to coordinates using
 * the best match from the marketplace's map provider.
 */
const geocode = (mapsConfig, locale, search) => {
  const cacheKey = search.trim().toLowerCase();
  if (!geocodeCache.has(cacheKey)) {
    const request = waitForGeocoder(mapsConfig)
      .then(geocoder =>
        geocoder
          .getPlacePredictions(search, null, locale)
          .then(({ predictions }) =>
            predictions?.length > 0 ? geocoder.getPlaceDetails(predictions[0]) : null
          )
      )
      .then(place => (place?.origin ? { origin: place.origin, address: place.address } : null));
    // Don't remember failures: the next listing view should get to try again.
    request.catch(() => geocodeCache.delete(cacheKey));
    geocodeCache.set(cacheKey, request);
  }
  return geocodeCache.get(cacheKey);
};

const IconDispatchPin = () => (
  <svg className={css.dispatchStopIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconHome = () => (
  <svg className={css.dispatchStopIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M4 11l8-6.5 8 6.5M6 9.5V19h12V9.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCrosshair = () => (
  <svg className={css.dispatchButtonIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 3v3M12 18v3M3 12h3M18 12h3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const JourneyMapImage = lazyLoadWithDimensions(
  props => {
    const { dimensions, mapsConfig, origin, destination, alt } = props;
    const src = staticJourneyMapUrl({
      mapsConfig,
      origin,
      destination,
      width: dimensions.width,
      height: dimensions.height,
    });
    return src ? (
      <img className={css.dispatchMapImage} src={src} alt={alt} crossOrigin="anonymous" />
    ) : null;
  },
  { maxWidth: `${STATIC_MAP_MAX_WIDTH}px` }
);

/**
 * Where a product is dispatched from, shown as a pin on a map. Shoppers can
 * add their own town or postcode (or use their device location) to see the
 * straight-line distance the order would travel to reach them.
 *
 * The location comes from the hosted "Where is this product dispatched from?"
 * listing field, which sellers fill in as free text. It's geocoded in the
 * browser; if that isn't possible (no map provider credentials, the place
 * can't be found) the section still shows the seller's text without a map.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.fieldConfig] hosted config of the dispatch location field
 * @param {Object} props.publicData listing.attributes.publicData
 * @param {Object} props.metadata listing.attributes.metadata
 * @param {Object} props.mapsConfig config.maps
 * @param {string} [props.locale] config.localization.locale
 * @returns {JSX.Element|null} null when the seller hasn't said where it ships from
 */
const SectionDispatchLocation = props => {
  const { fieldConfig, publicData, metadata, mapsConfig, locale } = props;
  const intl = useIntl();

  const entry = getListingFieldEntry(fieldConfig, publicData, metadata);
  const place = entry ? formatListingFieldValue(entry.value, fieldConfig).trim() : null;

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [search, setSearch] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [locateError, setLocateError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setOrigin(null);
    setDestination(null);
    if (!place) {
      return;
    }
    geocode(mapsConfig, locale, place)
      .then(result => {
        if (isMounted.current && result) {
          setOrigin(result.origin);
        }
      })
      .catch(() => {
        // No map: the seller's text on its own is still useful.
      });
  }, [place]);

  if (!place) {
    return null;
  }

  const showResult = (latlng, label) => {
    if (!isMounted.current) {
      return;
    }
    setIsLocating(false);
    setDestination({ origin: latlng, label });
  };
  const showError = messageId => {
    if (!isMounted.current) {
      return;
    }
    setIsLocating(false);
    setLocateError(messageId);
  };

  const handleSearch = e => {
    e.preventDefault();
    const query = search.trim();
    if (!query) {
      return;
    }
    setIsLocating(true);
    setLocateError(null);
    geocode(mapsConfig, locale, query)
      .then(result =>
        result
          ? showResult(result.origin, query)
          : showError('ListingPage.dispatchLocationNotFound')
      )
      .catch(() => showError('ListingPage.dispatchLocationNotFound'));
  };

  const handleUseMyLocation = () => {
    setIsLocating(true);
    setLocateError(null);
    userLocation()
      .then(latlng =>
        showResult(latlng, intl.formatMessage({ id: 'ListingPage.dispatchLocationYourLocation' }))
      )
      .catch(() => showError('ListingPage.dispatchLocationGeolocationFailed'));
  };

  const handleReset = () => {
    setDestination(null);
    setSearch('');
    setLocateError(null);
  };

  const miles =
    origin && destination ? roundMiles(distanceInMiles(origin, destination.origin)) : null;

  return (
    <section className={css.sectionDispatchLocation} id="dispatch-location">
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.dispatchLocationTitle" />
      </Heading>
      <p className={css.dispatchIntro}>
        <FormattedMessage id="ListingPage.dispatchLocationIntro" />
      </p>

      <div className={css.dispatchCard}>
        {origin ? (
          <div className={css.dispatchMap}>
            <JourneyMapImage
              mapsConfig={mapsConfig}
              origin={origin}
              destination={destination?.origin}
              alt={intl.formatMessage(
                { id: 'ListingPage.dispatchLocationMapAlt' },
                { place: place }
              )}
            />
          </div>
        ) : null}

        <div className={css.dispatchJourney}>
          <div className={css.dispatchStops}>
            <div className={css.dispatchStop}>
              <span className={css.dispatchStopIconWrapper}>
                <IconDispatchPin />
              </span>
              <div>
                <span className={css.dispatchStopLabel}>
                  <FormattedMessage id="ListingPage.dispatchLocationFromLabel" />
                </span>
                <span className={css.dispatchStopPlace}>{place}</span>
              </div>
            </div>

            {miles ? (
              <>
                <div className={css.dispatchDistance} aria-live="polite">
                  <span className={css.dispatchDistanceValue}>
                    <FormattedMessage id="ListingPage.dispatchLocationMiles" values={{ miles }} />
                  </span>
                  <span className={css.dispatchDistanceCaption}>
                    <FormattedMessage id="ListingPage.dispatchLocationAsTheCrowFlies" />
                  </span>
                </div>
                <div className={css.dispatchStop}>
                  <span className={css.dispatchStopIconWrapperInk}>
                    <IconHome />
                  </span>
                  <div>
                    <span className={css.dispatchStopLabel}>
                      <FormattedMessage id="ListingPage.dispatchLocationToLabel" />
                    </span>
                    <span className={css.dispatchStopPlace}>{destination.label}</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {origin && !miles ? (
            <form className={css.dispatchForm} onSubmit={handleSearch}>
              <label className={css.dispatchFormLabel} htmlFor="dispatch-location-search">
                <FormattedMessage id="ListingPage.dispatchLocationSearchLabel" />
              </label>
              <div className={css.dispatchFormRow}>
                <input
                  id="dispatch-location-search"
                  className={css.dispatchFormInput}
                  type="text"
                  autoComplete="postal-code"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'ListingPage.dispatchLocationSearchPlaceholder',
                  })}
                />
                <button
                  type="submit"
                  className={css.dispatchFormSubmit}
                  disabled={isLocating || !search.trim()}
                >
                  <FormattedMessage id="ListingPage.dispatchLocationSearchSubmit" />
                </button>
              </div>
              <button
                type="button"
                className={css.dispatchUseLocation}
                onClick={handleUseMyLocation}
                disabled={isLocating}
              >
                <IconCrosshair />
                <FormattedMessage id="ListingPage.dispatchLocationUseMyLocation" />
              </button>
              {locateError ? (
                <p className={css.dispatchError} role="alert">
                  <FormattedMessage id={locateError} />
                </p>
              ) : null}
            </form>
          ) : null}

          {miles ? (
            <button type="button" className={css.dispatchReset} onClick={handleReset}>
              <FormattedMessage id="ListingPage.dispatchLocationChange" />
            </button>
          ) : null}

          {origin ? (
            <p className={css.dispatchNote}>
              <FormattedMessage id="ListingPage.dispatchLocationNote" />
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default SectionDispatchLocation;
